import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { Home3Event } from '../../../lib/home3-feed';
import * as L from '../../../lib/home-map/phone-layout';
import { liveSheetSubtitle, panelRowText } from '../../../lib/home3-panel-row';
import { handleModalEscape, pushModalEscape } from '../../../lib/modal-escape';
import { useTranslation } from '../../../lib/i18n';
import { IconButton, ScrollArea, Typography } from '../ui';
import { Home3ActivityRow } from './Home3ActivityPanel';
import type { Home3Messenger } from './Home3Categories';
import styles from './Home3PhoneLiveSheet.module.css';

/** How far down (of the sheet's height) a drag must go to close it; less springs back. */
const DRAG_CLOSE_SHARE = 0.25;
/** A downward flick this fast (px/ms) closes it whatever the distance. */
const FLICK_SPEED = 0.8;

/**
 * **The Recent activity sheet** the map's Live Updates chip opens (Figma 2048:119655) — what the reader has not seen
 * since their last visit: the SAME rows the headline counts and the rail's badges add up to, oldest first, the
 * newest at the foot, as the desktop canvas's activity panel lists them (`Home3ActivityRow`, one row component for
 * both), scrolled to the newest.
 *
 * ## The live feed (the "Symphony Mobile" prototype's)
 *
 * On every open the newest `liveFeedPending` rows ARRIVE, one at a time; the older ones are already there. An
 * arrival enters at the FOOT and slides the stack up by its height while the row fades in — the rows sit at the bottom,
 * as desktop's activity panel's do — shows three dots, then types its text in, and
 * its link shows once the text is in; the next one comes when nothing is typing and the gap has passed
 * (`phone-layout.ts` `liveFeed*`, the phones' numbers). The list stays on the newest row unless the reader has
 * scrolled up. Under reduced motion every row is simply there.
 *
 * It rises from the bottom to `top` (the frame's y 224, under the island row), and the app's chat bar stays over
 * it, as the frame draws it: it sits a layer under the bar. Closed by ✕, Escape, a drag down, or a press above it;
 * closing logs nothing (the user's call). A row's link closes it and opens the row (`onOpenRow`, which the map
 * owns, so a drill-in the opener draws outlives the sheet). Mirrors iOS/Android `ActivityMapLiveSheet`.
 *
 * Portalled to `<body>`: the map's board is CSS-scaled, and a transform makes `position: fixed` relative to it.
 */
export function Home3PhoneLiveSheet({
  rows: liveRows,
  top,
  agentNames,
  maestroName,
  senders,
  onClose,
  onOpenRow,
}: {
  /** The unseen rows, oldest first. */
  rows: Home3Event[];
  /** The sheet's top edge, in viewport pixels. */
  top: number;
  agentNames: Record<string, string>;
  maestroName: string;
  senders?: Record<string, Home3Messenger>;
  onClose: () => void;
  /** A row's link was pressed: the sheet closes, then the map opens the row. */
  onOpenRow: (event: Home3Event) => void;
}) {
  const { t } = useTranslation();
  // ONE snapshot for as long as the sheet is open: the replay's pending count, what is shown and the next arrival all read it,
  // so a row that comes in meanwhile can't skip or reorder the newest-three arrivals. It shows the next time the sheet opens.
  const [rows] = useState(liveRows);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLElement | null>(null);
  const close = useRef(onClose);
  close.current = onClose;
  // Leaving runs the slide down first; `after` is what happens once it is out of sight.
  const [leaving, setLeaving] = useState<null | (() => void)>(null);
  const [drag, setDrag] = useState(0);
  const dragFrom = useRef<{ y: number; t: number; pointer: number } | null>(null);

  // ## The live feed: how many of the newest rows are still to arrive, and the one typing in now.
  const [pending] = useState(() => {
    const still = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    return still ? 0 : L.liveFeedPending(rows.length);
  });
  const [arrived, setArrived] = useState(0);
  const [typing, setTyping] = useState<{ id: string; at: number } | null>(null);
  const [, setTick] = useState(0);
  const lastArrival = useRef<number | null>(null);
  const openedAt = useRef(0);
  const stuck = useRef(true);
  const rowsRef = useRef<HTMLDivElement | null>(null);
  const waiting = pending - arrived;
  const shown = waiting > 0 ? rows.slice(0, Math.max(0, rows.length - waiting)) : rows;

  const leave = (after: () => void = () => close.current()) => setLeaving(() => after);

  // Escape belongs to the topmost layer: this sheet, while it is open.
  useEffect(() => {
    const pop = pushModalEscape(() => leave());
    const onKey = (e: KeyboardEvent) => handleModalEscape(e);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      pop();
    };
  }, []);

  // Focus moves into the sheet when it opens and back to the chip when it closes.
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    sheetRef.current?.focus();
    return () => {
      if (opener && opener.isConnected) opener.focus();
    };
  }, []);

  // It opens on the newest row, at the foot — and stays there as rows arrive and type in, unless the reader has
  // scrolled up.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (list && stuck.current) list.scrollTop = list.scrollHeight;
  });

  // The feed's clock: an arrival when one is due, the typing as it goes. It stops once everything has arrived.
  useEffect(() => {
    if (waiting <= 0 && !typing) return undefined;
    if (!openedAt.current) openedAt.current = performance.now();
    let raf = 0;
    let lastPaint = 0;
    const tick = (now: number) => {
      if (typing) {
        const event = rows.find((e) => e.id === typing.id);
        const text = event ? panelRowText(event, t) : '';
        const typed = L.liveFeedTyped(text, now - typing.at);
        if (typed !== null && typed >= text.length) {
          setTyping(null);
          return;
        }
        if (now - lastPaint > 45) {
          lastPaint = now;
          setTick((n) => n + 1);
        }
      } else if (waiting > 0) {
        const due = lastArrival.current === null ? openedAt.current + L.liveFeedStartMs : lastArrival.current + L.liveFeedGapMs;
        if (now >= due) {
          const next = rows[rows.length - waiting];
          lastArrival.current = now;
          setArrived((n) => n + 1);
          if (next) setTyping({ id: next.id, at: now });
          return;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [waiting, typing, rows, t]);

  // An arrival: the list slides up by the new row's height while the row fades in.
  useLayoutEffect(() => {
    if (!arrived) return;
    const box = rowsRef.current;
    const node = box?.lastElementChild as HTMLElement | null;
    if (!box || !node) return;
    const gap = parseFloat(getComputedStyle(box).rowGap) || 0;
    const easing = 'cubic-bezier(.2,.8,.2,1)';
    box.animate([{ transform: `translateY(${node.offsetHeight + gap}px)` }, { transform: 'translateY(0)' }], { duration: L.liveFeedSlideMs, easing });
    node.animate([{ opacity: 0 }, { opacity: 1 }], { duration: L.liveFeedFadeMs, delay: L.liveFeedFadeDelayMs, fill: 'backwards' });
  }, [arrived]);

  const onDragStart = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (leaving) return;
    dragFrom.current = { y: e.clientY, t: performance.now(), pointer: e.pointerId };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onDragMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const from = dragFrom.current;
    if (!from || from.pointer !== e.pointerId) return;
    setDrag(Math.max(0, e.clientY - from.y));
  };
  const onDragEnd = (e: ReactPointerEvent<HTMLDivElement>) => {
    const from = dragFrom.current;
    if (!from || from.pointer !== e.pointerId) return;
    dragFrom.current = null;
    const dy = Math.max(0, e.clientY - from.y);
    const speed = dy / Math.max(1, performance.now() - from.t);
    const height = sheetRef.current?.offsetHeight ?? 1;
    if (dy > height * DRAG_CLOSE_SHARE || speed > FLICK_SPEED) leave();
    else setDrag(0);
  };

  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className={styles.layer} data-testid="phone-map-live-sheet">
      {/* Paints nothing: a press above the sheet (the island row, the header) closes it. */}
      <div className={styles.catcher} onClick={() => leave()} aria-hidden data-sheet-catcher />
      <div
        ref={sheetRef}
        className={`${styles.sheet}${leaving ? ` ${styles.leaving}` : ''}`}
        style={{ top, ...(drag && !leaving ? { transform: `translateY(${drag}px)`, transition: 'none' } : {}) }}
        role="dialog"
        aria-label={t('home.overview.recentActivity')}
        tabIndex={-1}
        onAnimationEnd={(e) => {
          if (e.target === e.currentTarget && leaving) leaving();
        }}
      >
        {/* The grabber and the header are the handle a drag down closes the sheet by. */}
        <div className={styles.head} onPointerDown={onDragStart} onPointerMove={onDragMove} onPointerUp={onDragEnd} onPointerCancel={onDragEnd}>
          <span className={styles.grabber} aria-hidden />
          <div className={styles.titleRow}>
            <span className={styles.titles}>
              <Typography variant="h2" color="primary" component="h2" className={styles.title} dir="auto">
                {t('home.overview.recentActivity')}
              </Typography>
              <Typography variant="heading-04" color="primary" component="p" className={styles.subtitle} dir="auto">
                {liveSheetSubtitle(rows, t)}
              </Typography>
            </span>
            <IconButton size="large" aria-label={t('common.close')} onClick={() => leave()} onPointerDown={(e) => e.stopPropagation()}>
              <X size={24} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
            </IconButton>
          </div>
        </div>
        <div className={styles.listWrap}>
        <ScrollArea
          ref={listRef}
          className={styles.list}
          onScroll={(e) => {
            const el = e.currentTarget;
            stuck.current = el.scrollHeight - el.clientHeight - el.scrollTop < 24;
          }}
        >
          <div ref={rowsRef} className={styles.rows}>
            {shown.map((event) => {
              const text = panelRowText(event, t);
              const typed = typing?.id === event.id ? L.liveFeedTyped(text, performance.now() - typing.at) : undefined;
              return (
                <Home3ActivityRow
                  key={event.id}
                  event={event}
                  text={text}
                  typed={typed === undefined ? undefined : (typed ?? 0)}
                  typing={typed === null}
                  done={typed === undefined || (typed ?? 0) >= text.length}
                  agentNames={agentNames}
                  maestroName={maestroName}
                  senders={senders}
                  onOpen={() =>
                    leave(() => {
                      close.current();
                      onOpenRow(event);
                    })
                  }
                />
              );
            })}
          </div>
        </ScrollArea>
        {/* The top fade the older rows scroll up into. */}
        <span className={styles.fade} aria-hidden />
        </div>
      </div>
    </div>,
    document.body,
  );
}
